import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronLeft,
    User,
    Phone,
    Mail,
    FileText,
    Building,
    CheckCircle2,
    Check,
    Plus,
    Building2,
    MapPin,
    CreditCard,
    BadgeCheck,
    Briefcase,
    FileDigit,
    Layout,
    UploadCloud,
    CheckCircle,
    Navigation,
    Home,
    Map,
    Loader2
} from 'lucide-react';
import { vendorApi } from '../services/api';

function AddPartner() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [step, setStep] = useState(1);
    const [showToast, setShowToast] = useState(false);
    const [isStep3Saved, setIsStep3Saved] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [loadingExisting, setLoadingExisting] = useState(!!id);
    const [loadError, setLoadError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        // Step 1: Company Information
        companyLogo: null,
        companyName: '',
        contactPerson: '',
        email: '',
        contactNumber: '',
        // Step 2: License & Documents
        drugLicense1: '',
        drugLicense2: '',
        panNumber: '',
        fssaiNumber: '',
        gstNumber: '',
        gstAuthMethod: 'Manual', // UI-only — no backend field for this yet
        gstCertificate: null,     // UI-only — no upload endpoint yet
        // Step 3: Address
        buildingNumber: '',
        blockName: '',
        areaName: '',
        landmark: '',
        pincode: '',
        city: '',
        district: '',
        state: '',
        latitude: '28.613241',
        longitude: '77.381349'
    });

    // ── Load existing vendor (edit mode) from the API ──
    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        (async () => {
            setLoadingExisting(true);
            setLoadError(null);
            try {
                const v = await vendorApi.getVendor(id);
                if (cancelled) return;
                setFormData(prev => ({
                    ...prev,
                    companyName: v.companyName || '',
                    contactPerson: v.contactPersonName || '',
                    email: v.email || '',
                    contactNumber: v.contactNumber || '',
                    drugLicense1: v.drugLicensePart1 || '',
                    drugLicense2: v.drugLicensePart2 || '',
                    panNumber: v.panNumber || '',
                    fssaiNumber: v.fssaiNumber || '',
                    gstNumber: v.gstin || '',
                    buildingNumber: v.buildingNumber || '',
                    blockName: v.blockRoadName || '',
                    areaName: v.areaName || '',
                    pincode: v.areaPinCode || '',
                    city: v.cityTown || '',
                    district: v.district || '',
                    state: v.state || '',
                }));
                // Address was already saved server-side for an existing vendor
                setIsStep3Saved(true);
            } catch (err) {
                setLoadError(err.message || 'Failed to load vendor');
                console.error('Vendor load error:', err);
            } finally {
                if (!cancelled) setLoadingExisting(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, companyLogo: URL.createObjectURL(file) }));
        }
    };

    const isStep1Valid = formData.companyName && formData.contactPerson && formData.contactNumber;
    const isStep2Valid = formData.drugLicense1 && formData.drugLicense2 && formData.panNumber && formData.fssaiNumber && formData.gstNumber && (formData.gstAuthMethod === 'Manual' ? formData.gstCertificate : true);
    const isStep3Valid = formData.buildingNumber && formData.pincode && formData.city && formData.state && formData.latitude && formData.longitude;

    const steps = [
        { id: 1, label: 'Company Details' },
        { id: 2, label: 'License & Documents details' },
        { id: 3, label: 'Address Details' }
    ];

    // Map UI form fields → backend vendor payload (matches /api/vendors schema)
    const buildPayload = () => ({
        companyName: formData.companyName,
        contactPersonName: formData.contactPerson,
        email: formData.email,
        contactNumber: formData.contactNumber,
        panNumber: formData.panNumber,
        fssaiNumber: formData.fssaiNumber,
        drugLicensePart1: formData.drugLicense1,
        drugLicensePart2: formData.drugLicense2,
        gstin: formData.gstNumber,
        buildingNumber: formData.buildingNumber,
        blockRoadName: formData.blockName,
        areaName: formData.areaName,
        areaPinCode: formData.pincode,
        cityTown: formData.city,
        district: formData.district,
        state: formData.state,
    });

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            if (id) {
                await vendorApi.updateVendor(id, buildPayload());
            } else {
                await vendorApi.createVendor(buildPayload());
            }
            setToastMessage(`${id ? 'Vendor updated' : 'New Vendor added'} successfully!`);
            setShowToast(true);
            setTimeout(() => {
                setShowToast(false);
                navigate('/b2b-partners');
            }, 2000);
        } catch (err) {
            alert(err.message || 'Failed to save vendor');
        } finally {
            setSubmitting(false);
        }
    };

    // Address is a client-side step-gate only — no dedicated "save address" endpoint,
    // the full payload is sent together on Final Submit above.
    const handleSaveAddress = () => {
        if (isStep3Valid) {
            setIsStep3Saved(true);
            setToastMessage('Address details saved successfully!');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 2000);
        }
    };

    if (loadingExisting) {
        return (
            <div className="bg-[#F4F9F8] min-h-screen flex items-center justify-center">
                <div className="flex items-center gap-3 text-[#1E4D43]">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-bold">Loading vendor...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#F4F9F8] min-h-screen p-8 font-sans pb-32">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-10">
                <button
                    onClick={() => navigate(-1)}
                    className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-[#1E4D43] text-[#1E4D43] hover:bg-[#1E4D43] hover:text-white transition-all"
                >
                    <ChevronLeft size={20} />
                </button>
                <h1 className="text-[22px] font-bold text-[#1E4D43]">{id ? 'Edit Vendor' : 'Add new Vendor'}</h1>
            </div>

            {loadError && (
                <div className="max-w-4xl mx-auto mb-8 flex items-center gap-3 p-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold">
                    <span>{loadError}</span>
                </div>
            )}

            {/* Stepper */}
            <div className="flex items-center justify-center mb-12 max-w-4xl mx-auto">
                {steps.map((s, idx) => (
                    <React.Fragment key={s.id}>
                        <div className="flex flex-col items-center relative z-10">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold transition-all border-4 shadow-sm ${step >= s.id ? 'bg-[#4B9C86] border-[#D1E7E1] text-white' : 'bg-[#F2F4F7] border-white text-gray-400'
                                }`}>
                                {s.id}
                            </div>
                            <div className="mt-2 text-center">
                                <p className={`text-[12px] font-medium ${step >= s.id ? 'text-[#1E4D43]' : 'text-gray-400'}`}>Step {s.id}</p>
                                <p className={`text-[13px] font-bold whitespace-nowrap ${step >= s.id ? 'text-[#1E4D43]' : 'text-gray-400'}`}>{s.label}</p>
                            </div>
                        </div>
                        {idx < steps.length - 1 && (
                            <div className={`flex-1 h-[2px] mb-8 mx-4 transition-all ${step > s.id ? 'bg-[#4B9C86]' : 'bg-gray-200'}`} />
                        )}
                    </React.Fragment>
                ))}
            </div>

            <div className="flex flex-col lg:flex-row gap-8 max-w-[1400px] mx-auto">
                <div className="flex-1">
                    <div className="bg-white rounded-[2rem] p-10 shadow-sm border border-gray-100 min-h-[550px] flex flex-col">
                        {step === 1 && (
                            <div className="animate-in fade-in duration-500 flex flex-col flex-1">
                                <h2 className="text-[26px] font-bold text-[#1E4D43] mb-8">Company Information</h2>
                                <div className="mb-10">
                                    <p className="text-[15px] font-bold text-gray-600 mb-3">Company logo</p>
                                    <div
                                        onClick={() => document.getElementById('logo-upload').click()}
                                        className="w-20 h-20 rounded-full bg-[#EBF5F1] border-2 border-[#4B9C86] flex items-center justify-center relative cursor-pointer overflow-hidden group"
                                    >
                                        {formData.companyLogo ? (
                                            <img src={formData.companyLogo} alt="Logo" className="w-full h-full object-cover" />
                                        ) : (
                                            <Building2 size={32} className="text-[#4B9C86]" />
                                        )}
                                        <div className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-sm border border-[#4B9C86]">
                                            <Plus size={14} className="text-[#4B9C86]" />
                                        </div>
                                        <input type="file" id="logo-upload" hidden onChange={handleFileChange} accept="image/*" />
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-2">Logo upload has no backend endpoint yet — stored locally only.</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                                    <FormField
                                        label="Company name *"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        placeholder="Enter name"
                                        icon={<Building size={18} className="text-[#4B9C86]" />}
                                    />
                                    <FormField
                                        label="Contact person name *"
                                        name="contactPerson"
                                        value={formData.contactPerson}
                                        onChange={handleChange}
                                        placeholder="Enter name"
                                        icon={<User size={18} className="text-[#3B82F6]" />}
                                    />
                                    <FormField
                                        label="Email Address"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Enter email"
                                        icon={<Mail size={18} className="text-[#F97316]" />}
                                    />
                                    <FormField
                                        label="Contact number *"
                                        name="contactNumber"
                                        value={formData.contactNumber}
                                        onChange={handleChange}
                                        placeholder="Enter number"
                                        icon={<Phone size={18} className="text-[#10B981]" />}
                                    />
                                </div>

                                <div className="mt-auto flex justify-end pt-10">
                                    <button
                                        onClick={() => setStep(2)}
                                        disabled={!isStep1Valid}
                                        className={`px-12 py-3.5 rounded-xl font-bold text-[16px] transition-all shadow-md ${isStep1Valid ? 'bg-[#BDDBCE] text-[#1E4D43] hover:bg-[#A9CCBD]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="animate-in fade-in duration-500 flex flex-col flex-1 pb-4">
                                {/* Drug License Section */}
                                <div className="mb-10">
                                    <h3 className="text-[22px] font-bold text-[#1E4D43] mb-6 flex items-center">
                                        Drug licence details
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                        <FormField
                                            label="Part 1 *"
                                            name="drugLicense1"
                                            value={formData.drugLicense1}
                                            onChange={handleChange}
                                            placeholder="UP-1234566XXXX"
                                            icon={<div className="w-6 h-6 bg-[#EBF5F1] rounded flex items-center justify-center"><FileDigit size={14} className="text-[#4B9C86]" /></div>}
                                        />
                                        <FormField
                                            label="Part 2 *"
                                            name="drugLicense2"
                                            value={formData.drugLicense2}
                                            onChange={handleChange}
                                            placeholder="UP-1234566XXXX"
                                            icon={<div className="w-6 h-6 bg-[#EBF5F1] rounded flex items-center justify-center"><FileDigit size={14} className="text-[#4B9C86]" /></div>}
                                        />
                                    </div>
                                </div>

                                {/* Documents Section */}
                                <div className="mb-10">
                                    <h3 className="text-[22px] font-bold text-[#1E4D43] mb-6">Documents details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                        <FormField
                                            label="Pan number *"
                                            name="panNumber"
                                            value={formData.panNumber}
                                            onChange={handleChange}
                                            placeholder="XXXXXX733A"
                                            icon={<CreditCard size={18} className="text-[#3B82F6]" />}
                                        />
                                        <FormField
                                            label="FSSAI number *"
                                            name="fssaiNumber"
                                            value={formData.fssaiNumber}
                                            onChange={handleChange}
                                            placeholder="XXXXXXXXXXXX175"
                                            icon={<BadgeCheck size={18} className="text-[#F59E0B]" />}
                                        />
                                    </div>
                                </div>

                                {/* GST Authentication Section */}
                                <div className="mb-8">
                                    <h3 className="text-[22px] font-bold text-[#1E4D43] mb-6">GST Authentication</h3>

                                    <p className="text-[13px] font-bold text-gray-700 mb-3">Authentication by</p>
                                    <div className="flex gap-4 mb-8">
                                        <label className={`flex-1 flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.gstAuthMethod === 'Automatic' ? 'border-[#4B9C86] bg-[#F2FBF9]' : 'border-gray-100 bg-[#F9FBFA]'}`}>
                                            <input
                                                type="radio"
                                                name="gstAuthMethod"
                                                value="Automatic"
                                                checked={formData.gstAuthMethod === 'Automatic'}
                                                onChange={handleChange}
                                                className="hidden"
                                            />
                                            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${formData.gstAuthMethod === 'Automatic' ? 'border-[#4B9C86]' : 'border-gray-300'}`}>
                                                {formData.gstAuthMethod === 'Automatic' && <div className="w-2.5 h-2.5 rounded-full bg-[#4B9C86]" />}
                                            </div>
                                            <span className="font-bold text-[14px] text-gray-700">Automatic</span>
                                        </label>

                                        <label className={`flex-1 flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.gstAuthMethod === 'Manual' ? 'border-[#4B9C86] bg-[#F2FBF9]' : 'border-gray-100 bg-[#F9FBFA]'}`}>
                                            <input
                                                type="radio"
                                                name="gstAuthMethod"
                                                value="Manual"
                                                checked={formData.gstAuthMethod === 'Manual'}
                                                onChange={handleChange}
                                                className="hidden"
                                            />
                                            <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${formData.gstAuthMethod === 'Manual' ? 'border-[#4B9C86]' : 'border-gray-300'}`}>
                                                {formData.gstAuthMethod === 'Manual' && <div className="w-2.5 h-2.5 rounded-full bg-[#4B9C86]" />}
                                            </div>
                                            <span className="font-bold text-[14px] text-gray-700">Manual</span>
                                        </label>
                                    </div>

                                    <div className={`grid grid-cols-1 ${formData.gstAuthMethod === 'Manual' ? 'md:grid-cols-2' : ''} gap-x-8 gap-y-8`}>
                                        <FormField
                                            label="GST number *"
                                            name="gstNumber"
                                            value={formData.gstNumber}
                                            onChange={handleChange}
                                            placeholder="22AAAAA0000A1Z5"
                                            icon={<div className="w-6 h-6 flex items-center justify-center"><Building size={18} className="text-[#1E4D43]" /></div>}
                                        />

                                        {formData.gstAuthMethod === 'Manual' && (
                                            <div className="space-y-4">
                                                <p className="text-[13px] font-bold text-gray-700">GST Certificate *</p>
                                                <div
                                                    onClick={() => document.getElementById('gst-upload').click()}
                                                    className="border-2 border-dashed border-[#E1F2ED] rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-all bg-white group min-h-[140px]"
                                                >
                                                    {formData.gstCertificate ? (
                                                        <div className="flex flex-col items-center">
                                                            <div className="w-10 h-10 bg-[#EBF5F1] rounded-full flex items-center justify-center text-[#4B9C86] mb-2">
                                                                <CheckCircle size={20} />
                                                            </div>
                                                            <p className="text-[12px] font-bold text-[#1E4D43]">Certificate Uploaded</p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <UploadCloud size={32} className="text-[#4B9C86] mb-3 group-hover:scale-110 transition-transform" />
                                                            <p className="font-bold text-[14px] text-gray-700">Upload Certificate</p>
                                                            <p className="text-[11px] text-gray-400 mt-1">Only PNG, JPG or JPEG files are accepted</p>
                                                        </>
                                                    )}
                                                    <input
                                                        type="file"
                                                        id="gst-upload"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) setFormData(prev => ({ ...prev, gstCertificate: URL.createObjectURL(file) }));
                                                        }}
                                                    />
                                                </div>
                                                <p className="text-[10px] text-gray-400">No certificate-upload endpoint yet — stored locally only.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-auto flex justify-end space-x-4 pt-10">
                                    <button
                                        onClick={() => setStep(1)}
                                        className="px-8 py-3.5 rounded-xl font-bold text-[16px] bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all shadow-sm"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={() => setStep(3)}
                                        disabled={!isStep2Valid}
                                        className={`px-12 py-3.5 rounded-xl font-bold text-[16px] transition-all shadow-md ${isStep2Valid ? 'bg-[#BDDBCE] text-[#1E4D43] hover:bg-[#A9CCBD]' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="animate-in fade-in duration-500 flex flex-col flex-1 pb-4">
                                <h2 className="text-[26px] font-bold text-[#1E4D43] mb-6">Address</h2>

                                {/* Map Preview Container */}
                                <div className="mb-10 relative group">
                                    <div className="w-full h-[220px] rounded-[1.5rem] overflow-hidden border-2 border-[#E1F2ED] shadow-sm relative bg-[#EDF2F1] flex items-center justify-center">
                                        <Map size={40} className="text-[#4B9C86]/40" />
                                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-lg text-[#4B9C86] shadow-sm cursor-pointer hover:bg-white transition-all">
                                            <Navigation size={20} />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-8">
                                    <FormField
                                        label="Latitude"
                                        name="latitude"
                                        value={formData.latitude}
                                        onChange={handleChange}
                                        placeholder="28.613241"
                                        icon={<div className="w-6 h-6 bg-[#EDF2F1] rounded flex items-center justify-center"><Navigation size={14} className="text-gray-500 rotate-45" /></div>}
                                    />
                                    <FormField
                                        label="Longitude"
                                        name="longitude"
                                        value={formData.longitude}
                                        onChange={handleChange}
                                        placeholder="77.381349"
                                        icon={<div className="w-6 h-6 bg-[#EDF2F1] rounded flex items-center justify-center"><Navigation size={14} className="text-gray-500 rotate-45" /></div>}
                                    />
                                </div>

                                <div className="border-t border-gray-50 pt-8 space-y-8">
                                    <FormField
                                        label="Building Number, Block, Area"
                                        name="buildingNumber"
                                        value={formData.buildingNumber}
                                        onChange={handleChange}
                                        placeholder="23, Block E, Sector 63"
                                        icon={<div className="w-6 h-6 bg-[#F5F3FF] rounded flex items-center justify-center"><Home size={16} className="text-[#8B5CF6]" /></div>}
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                        <FormField
                                            label="Landmark"
                                            name="landmark"
                                            value={formData.landmark}
                                            onChange={handleChange}
                                            placeholder="Police Chawki"
                                            icon={<div className="w-6 h-6 bg-[#FFF7ED] rounded flex items-center justify-center"><MapPin size={16} className="text-[#F97316]" /></div>}
                                        />
                                        <FormField
                                            label="Area pin code *"
                                            name="pincode"
                                            value={formData.pincode}
                                            onChange={handleChange}
                                            placeholder="201014"
                                            icon={<div className="w-6 h-6 bg-[#FFF7ED] rounded flex items-center justify-center"><MapPin size={16} className="text-[#F97316]" /></div>}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                        <FormField
                                            label="City/Town *"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            placeholder="Noida"
                                            icon={<div className="w-6 h-6 bg-[#ECFDF5] rounded flex items-center justify-center"><Map size={16} className="text-[#10B981]" /></div>}
                                        />
                                        <FormField
                                            label="District *"
                                            name="district"
                                            value={formData.district}
                                            onChange={handleChange}
                                            placeholder="Gautam Buddha Nagar"
                                            icon={<div className="w-6 h-6 bg-[#F8FAFC] rounded flex items-center justify-center"><Navigation size={14} className="text-gray-400 rotate-45" /></div>}
                                        />
                                    </div>

                                    <div className="w-full">
                                        <FormField
                                            label="State *"
                                            name="state"
                                            value={formData.state}
                                            onChange={handleChange}
                                            placeholder="Uttar Pradesh"
                                            icon={<div className="w-6 h-6 bg-[#FEF2F2] rounded flex items-center justify-center text-[#EF4444]"><BadgeCheck size={16} /></div>}
                                        />
                                    </div>
                                </div>

                                <div className="mt-auto flex justify-end space-x-4 pt-12">
                                    <button
                                        onClick={() => {
                                            setStep(2);
                                            setIsStep3Saved(false);
                                        }}
                                        className="px-10 py-3.5 rounded-xl font-bold text-[16px] bg-white border border-gray-200 text-gray-400 hover:bg-gray-50 transition-all shadow-sm"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={handleSaveAddress}
                                        disabled={!isStep3Valid}
                                        className={`px-12 py-3.5 rounded-xl font-bold text-[16px] transition-all shadow-md ${isStep1Valid && isStep2Valid && isStep3Valid ? 'bg-[#4B9C86] text-white hover:bg-[#3D8572]' : 'bg-gray-100 text-gray-300 cursor-not-allowed'}`}
                                    >
                                        {isStep3Saved ? 'Saved' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Registration Record Column */}
                <div className="w-full lg:w-[550px]">
                    <div className="bg-white rounded-[2rem] p-8 border-2 border-[#D1E7E1] h-full shadow-sm">
                        <div className="flex flex-col items-center mb-10">
                            <h3 className="text-center font-black text-[20px] text-[#1E4D43] mb-6 tracking-wide underline underline-offset-8 decoration-[#BDDBCE]">VENDORS REGISTRATION RECORD</h3>
                        </div>

                        <div className="space-y-6">
                            <RecordSection
                                title="1. VENDOR INFORMATION"
                                isCompleted={isStep1Valid}
                                rows={[
                                    { label: 'Company Name', value: formData.companyName },
                                    { label: 'Contact Person', value: formData.contactPerson },
                                    { label: 'Phone number', value: formData.contactNumber },
                                    { label: 'Email Address', value: formData.email }
                                ]}
                            />

                            <RecordSection
                                title="2. LICENSING & DOCUMENTS DETAILS"
                                isCompleted={isStep2Valid}
                                rows={[
                                    { label: 'DRUG License No. part 1', value: formData.drugLicense1 },
                                    { label: 'DRUG License No. part 2', value: formData.drugLicense2 },
                                    { label: 'PAN Number', value: formData.panNumber },
                                    { label: 'FSSAI Number', value: formData.fssaiNumber },
                                    { label: 'GST Number', value: formData.gstNumber }
                                ]}
                            />

                            <RecordSection
                                title="3. BILLING ADDRESS"
                                isCompleted={isStep3Saved}
                                rows={[
                                    { label: 'Building Number, Block, Area', value: formData.buildingNumber },
                                    { label: 'Landmark', value: formData.landmark },
                                    { label: 'Area Pin Code', value: formData.pincode },
                                    { label: 'City', value: formData.city },
                                    { label: 'District', value: formData.district },
                                    { label: 'State', value: formData.state }
                                ]}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Footer Action */}
            <div className="fixed bottom-0 left-0 md:left-64 right-0 p-6 flex justify-end px-20 z-[50]">
                <button
                    onClick={handleSubmit}
                    disabled={!isStep1Valid || !isStep2Valid || !isStep3Saved || submitting}
                    className={`flex items-center space-x-3 px-10 py-3 rounded-xl font-black text-lg transition-all shadow-lg ${isStep1Valid && isStep2Valid && isStep3Saved && !submitting
                        ? 'bg-[#1E4D43] text-white hover:scale-105 active:scale-95'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-70'
                        }`}
                >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isStep1Valid && isStep2Valid && isStep3Saved ? 'bg-white text-[#1E4D43]' : 'bg-gray-300'}`}>
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                    </div>
                    <span className="uppercase tracking-wider">
                        {submitting ? 'Saving...' : (id ? 'Update Partner' : 'Final Submit')}
                    </span>
                </button>
            </div>

            {/* Success Toast */}
            {showToast && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center">
                    <div className="bg-black/20 backdrop-blur-sm absolute inset-0" />
                    <div className="bg-white px-10 py-5 rounded-[2rem] shadow-2xl relative animate-in zoom-in duration-300 border border-teal-100">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-[#EBF5F1] rounded-full flex items-center justify-center text-[#4B9C86]">
                                <CheckCircle2 size={32} />
                            </div>
                            <div>
                                <h3 className="font-bold text-xl text-[#1E4D43]">{toastMessage.includes('Address') ? 'Progress Saved!' : 'Success!'}</h3>
                                <p className="text-gray-500">{toastMessage}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function FormField({ label, name, value, onChange, placeholder, icon }) {
    return (
        <div className="space-y-2">
            <label className="text-[14px] font-bold text-gray-700">{label}</label>
            <div className="relative">
                <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                    {icon}
                </div>
                <input
                    type="text"
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    className="w-full pl-12 pr-4 py-3 bg-[#F2FBF9] border border-[#E1F2ED] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4B9C86]/20 focus:border-[#4B9C86] transition-all placeholder:text-gray-300 font-medium text-[#1E4D43]"
                />
            </div>
        </div>
    );
}

function RecordSection({ title, rows, isCompleted }) {
    return (
        <div className="space-y-1">
            <div className={`py-2.5 px-4 rounded-md mb-2 flex items-center justify-between transition-colors ${isCompleted ? 'bg-[#D1E7E1]' : 'bg-[#F2F4F7]'}`}>
                <h4 className={`text-[15px] font-bold uppercase tracking-tight ${isCompleted ? 'text-[#1E4D43]' : 'text-gray-400'}`}>{title}</h4>
                {isCompleted && (
                    <div className="w-5 h-5 bg-[#1E4D43] rounded-full flex items-center justify-center text-white scale-in-center">
                        <Check size={12} strokeWidth={4} />
                    </div>
                )}
            </div>
            <div className="border border-[#E1F2ED] rounded-xl overflow-hidden divide-y divide-[#E1F2ED]">
                {rows.map((row, idx) => (
                    <div key={idx} className="flex min-h-[44px] items-center">
                        <div className="w-[220px] bg-[#F7FBFA] px-4 py-2 border-r border-[#E1F2ED] h-full flex items-center">
                            <span className="text-[14px] font-bold text-gray-600 leading-tight">{row.label}</span>
                        </div>
                        <div className="flex-1 px-4 py-2 bg-white flex items-center">
                            <span className="text-[14px] font-medium text-[#1E4D43] truncate">{row.value || '————'}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default AddPartner;